import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { getFiscalCalendar, fiscalYearOf, fiscalYearRange, fiscalQuarterRange, fiscalSpanLabel, } from '@techstack/core';
const PRESETS = [
    { value: '1m', label: '1m', days: 30 },
    { value: '3m', label: '3m', days: 90 },
    { value: '6m', label: '6m', days: 182 },
    { value: '1y', label: '1y', days: 365 },
    { value: '2y', label: '2y', days: 730 },
    { value: '3y', label: '3y', days: 1095 },
    { value: '5y', label: '5y', days: 1825 },
    { value: 'all', label: 'all', days: null },
];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
// The fiscal boundary is NOT a constant any more — it comes from the calendar
// row passed in (default: California state, Jul 1 – Jun 30, labeled by ending
// year, so FY26 = Jul 2025 – Jun 2026). See @techstack/core `fiscal`.
// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function formatDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
function parseDate(s) {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
}
function addDays(d, n) {
    const r = new Date(d);
    r.setDate(r.getDate() + n);
    return r;
}
function startOfMonth(d) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();
}
function isInRange(d, start, end) {
    if (!start || !end)
        return false;
    const t = d.getTime();
    return t >= start.getTime() && t <= end.getTime();
}
/**
 * Convert preset value to years (DERIVED value for backend compat)
 */
function presetToYears(preset) {
    if (preset === 'all' || preset === 'custom')
        return null;
    const match = preset.match(/^(\d+)(m|y)$/);
    if (!match)
        return null;
    const num = parseInt(match[1], 10);
    const unit = match[2];
    if (unit === 'y')
        return num;
    // Months: collapse to years, min 1 (lossy for sub-year)
    if (unit === 'm')
        return num >= 12 ? Math.floor(num / 12) : 1;
    return null;
}
/**
 * Derive years from custom date range
 */
function deriveYearsFromRange(startDate, endDate) {
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0)
        return null;
    const years = Math.ceil(diffDays / 365);
    return years || 1;
}
/** Parse a YYYY-MM-DD from the registry into a LOCAL Date (never UTC). */
function localDate(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
}
/** Current fiscal year on the given calendar. */
function getCurrentFiscalYear(cal) {
    return fiscalYearOf(new Date(), cal);
}
/**
 * Quarter start/end for a fiscal year. Q1 always begins on the calendar's FY
 * start month — state Q1 is Jul–Sep, PANW Q1 is Aug–Oct. Derived, not listed.
 */
function getFiscalQuarterRange(fy, quarter, cal) {
    const { start, end } = fiscalQuarterRange(fy, quarter, cal);
    return { start: localDate(start), end: localDate(end) };
}
/** Full fiscal year range on the given calendar. */
function getFiscalYearRange(fy, cal) {
    const { start, end } = fiscalYearRange(fy, cal);
    return { start: localDate(start), end: localDate(end) };
}
/**
 * Format date range for display (e.g., "Jun 1 – Jun 22, 2026")
 */
function formatDateRangeDisplay(startDate, endDate) {
    if (!startDate || !endDate)
        return 'All time';
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();
    if (sameMonth) {
        return `${MONTH_NAMES[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
    }
    if (sameYear) {
        return `${MONTH_NAMES[start.getMonth()]} ${start.getDate()} – ${MONTH_NAMES[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
    }
    return `${MONTH_NAMES[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()} – ${MONTH_NAMES[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}
/**
 * Calculate day count between two dates
 */
function getDayCount(startDate, endDate) {
    const start = parseDate(startDate);
    const end = parseDate(endDate);
    return Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
}
function CalendarMonth({ month, rangeStart, rangeEnd, hoverDate, onDayClick, onDayHover }) {
    const firstDay = startOfMonth(month);
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const startDayOfWeek = firstDay.getDay();
    const days = [];
    // Pad start
    for (let i = 0; i < startDayOfWeek; i++)
        days.push(null);
    // Actual days
    for (let i = 1; i <= daysInMonth; i++) {
        days.push(new Date(month.getFullYear(), month.getMonth(), i));
    }
    // Pad end to fill last row
    while (days.length % 7 !== 0)
        days.push(null);
    // Determine effective range for highlighting (use hover for preview)
    let effectiveStart = rangeStart;
    let effectiveEnd = rangeEnd;
    if (rangeStart && !rangeEnd && hoverDate) {
        if (hoverDate < rangeStart) {
            effectiveStart = hoverDate;
            effectiveEnd = rangeStart;
        }
        else {
            effectiveEnd = hoverDate;
        }
    }
    return (_jsxs("div", { style: calStyles.month, children: [_jsxs("div", { style: calStyles.monthHeader, children: [MONTH_NAMES[month.getMonth()], " ", month.getFullYear()] }), _jsx("div", { style: calStyles.weekdays, children: WEEKDAY_LABELS.map(d => (_jsx("div", { style: calStyles.weekday, children: d }, d))) }), _jsx("div", { style: calStyles.days, children: days.map((d, i) => {
                    if (!d)
                        return _jsx("div", { style: calStyles.dayEmpty }, i);
                    const isStart = rangeStart && isSameDay(d, rangeStart);
                    const isEnd = rangeEnd && isSameDay(d, rangeEnd);
                    const inRange = effectiveStart && effectiveEnd && isInRange(d, effectiveStart, effectiveEnd);
                    const isToday = isSameDay(d, new Date());
                    return (_jsxs("button", { type: "button", style: {
                            ...calStyles.day,
                            ...(inRange && !isStart && !isEnd ? calStyles.dayInRange : {}),
                            ...(isStart || isEnd ? calStyles.dayEndpoint : {}),
                        }, onClick: () => onDayClick(d), onMouseEnter: () => onDayHover(d), onMouseLeave: () => onDayHover(null), children: [_jsx("span", { children: d.getDate() }), isToday && !isStart && !isEnd && _jsx("span", { style: calStyles.todayDot })] }, i));
                }) })] }));
}
const calStyles = {
    month: {
        width: '220px',
        flexShrink: 0,
    },
    monthHeader: {
        textAlign: 'center',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '8px',
        color: 'var(--text-primary, #1f2937)',
    },
    weekdays: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '2px',
        marginBottom: '4px',
    },
    weekday: {
        textAlign: 'center',
        fontSize: '10px',
        fontWeight: 500,
        color: 'var(--text-muted, #6b7280)',
        padding: '2px 0',
    },
    days: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '2px',
    },
    dayEmpty: {
        padding: '6px',
    },
    day: {
        padding: '4px 6px 2px',
        border: 'none',
        borderRadius: '4px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '12px',
        color: 'var(--text-primary, #1f2937)',
        transition: 'all 0.1s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '28px',
    },
    dayInRange: {
        background: 'var(--accent-light, rgba(59, 130, 246, 0.15))',
    },
    dayEndpoint: {
        background: 'var(--accent, #3b82f6)',
        color: 'white',
        fontWeight: 600,
    },
    todayDot: {
        width: '4px',
        height: '4px',
        borderRadius: '50%',
        background: 'var(--accent, #3b82f6)',
        marginTop: '2px',
    },
};
// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
export function TimeRangePicker({ value, onChange, size = 'sm', className = '', disabled = false, fiscalCalendar, }) {
    // Active preset (null if custom range selected)
    const activePreset = value?.preset || '3y';
    // Popover state
    const [popoverOpen, setPopoverOpen] = useState(false);
    const popoverRef = useRef(null);
    const customBtnRef = useRef(null);
    // Calendar state
    const [viewMonth, setViewMonth] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1); // Show last month + current month
        return startOfMonth(d);
    });
    const [rangeStart, setRangeStart] = useState(null);
    const [rangeEnd, setRangeEnd] = useState(null);
    const [hoverDate, setHoverDate] = useState(null);
    // Which fiscal calendar the FY jumps mean. Absent → California state.
    const cal = fiscalCalendar ?? getFiscalCalendar();
    // FY state
    const [fyYear, setFyYear] = useState(() => getCurrentFiscalYear(cal));
    // Track selection source for range summary label
    const [selectionLabel, setSelectionLabel] = useState(null);
    // Close popover when clicking outside
    useEffect(() => {
        if (!popoverOpen)
            return;
        const handleClick = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target) &&
                customBtnRef.current && !customBtnRef.current.contains(e.target)) {
                setPopoverOpen(false);
                resetCalendarState();
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [popoverOpen]);
    const resetCalendarState = useCallback(() => {
        setRangeStart(null);
        setRangeEnd(null);
        setHoverDate(null);
        setFyYear(getCurrentFiscalYear(cal));
        setSelectionLabel(null);
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        setViewMonth(startOfMonth(d));
    }, [cal]);
    // Handle preset click (immediate commit)
    const handlePresetClick = useCallback((preset) => {
        if (disabled)
            return;
        const today = new Date();
        let startDate = null;
        let endDate = formatDate(today);
        if (preset.days === null) {
            startDate = null;
            endDate = null;
        }
        else {
            startDate = formatDate(addDays(today, -preset.days));
        }
        onChange({
            startDate,
            endDate,
            years: presetToYears(preset.value),
            preset: preset.value,
        });
        setPopoverOpen(false);
        resetCalendarState();
    }, [disabled, onChange, resetCalendarState]);
    // Handle day click in calendar
    const handleDayClick = useCallback((d) => {
        setSelectionLabel('Custom');
        if (!rangeStart || (rangeStart && rangeEnd)) {
            // First click or reset
            setRangeStart(d);
            setRangeEnd(null);
        }
        else {
            // Second click - auto-order
            if (d < rangeStart) {
                setRangeEnd(rangeStart);
                setRangeStart(d);
            }
            else {
                setRangeEnd(d);
            }
        }
    }, [rangeStart, rangeEnd]);
    // Handle FY quarter click
    const handleFyQuarterClick = useCallback((quarter) => {
        const { start, end } = getFiscalQuarterRange(fyYear, quarter, cal);
        setRangeStart(start);
        setRangeEnd(end);
        setSelectionLabel(`FY${String(fyYear).slice(-2)} Q${quarter}`);
        // Move view to show the quarter
        setViewMonth(startOfMonth(start));
    }, [fyYear, cal]);
    // Handle full FY click
    const handleFullFyClick = useCallback(() => {
        const { start, end } = getFiscalYearRange(fyYear, cal);
        setRangeStart(start);
        setRangeEnd(end);
        setSelectionLabel(`FY${String(fyYear).slice(-2)}`);
        setViewMonth(startOfMonth(start));
    }, [fyYear, cal]);
    // Handle Apply (custom range commit)
    const handleApply = useCallback(() => {
        if (!rangeStart || !rangeEnd)
            return;
        const startDate = formatDate(rangeStart);
        const endDate = formatDate(rangeEnd);
        onChange({
            startDate,
            endDate,
            years: deriveYearsFromRange(startDate, endDate),
            preset: 'custom',
        });
        setPopoverOpen(false);
        resetCalendarState();
    }, [rangeStart, rangeEnd, onChange, resetCalendarState]);
    // Handle Cancel
    const handleCancel = useCallback(() => {
        setPopoverOpen(false);
        resetCalendarState();
    }, [resetCalendarState]);
    // Navigate months
    const handlePrevMonth = useCallback(() => {
        setViewMonth(prev => {
            const d = new Date(prev);
            d.setMonth(d.getMonth() - 1);
            return d;
        });
    }, []);
    const handleNextMonth = useCallback(() => {
        setViewMonth(prev => {
            const d = new Date(prev);
            d.setMonth(d.getMonth() + 1);
            return d;
        });
    }, []);
    // Second month (one month after viewMonth)
    const secondMonth = useMemo(() => {
        const d = new Date(viewMonth);
        d.setMonth(d.getMonth() + 1);
        return d;
    }, [viewMonth]);
    const isSm = size === 'sm';
    // Display text for custom selection
    const displayText = useMemo(() => {
        if (activePreset === 'custom' && value?.startDate && value?.endDate) {
            const start = parseDate(value.startDate);
            const end = parseDate(value.endDate);
            return `${MONTH_NAMES[start.getMonth()]} ${start.getDate()} – ${MONTH_NAMES[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
        }
        return null;
    }, [activePreset, value]);
    // Resolved date display (shows what the current selection resolves to)
    const resolvedDateDisplay = useMemo(() => {
        if (!value?.startDate || !value?.endDate) {
            return { text: 'All time', days: null };
        }
        const text = formatDateRangeDisplay(value.startDate, value.endDate);
        const days = getDayCount(value.startDate, value.endDate);
        return { text, days };
    }, [value]);
    return (_jsxs("div", { style: { ...styles.wrapper, ...(disabled ? styles.disabled : {}) }, className: className, children: [_jsxs("div", { style: styles.container, children: [PRESETS.map(p => {
                        const isActive = activePreset === p.value;
                        return (_jsx("button", { style: {
                                ...styles.button,
                                ...(isSm ? styles.buttonSm : styles.buttonMd),
                                ...(isActive ? styles.buttonActive : {}),
                            }, onClick: () => handlePresetClick(p), disabled: disabled, type: "button", "aria-pressed": isActive, children: p.label }, p.value));
                    }), _jsxs("button", { ref: customBtnRef, style: {
                            ...styles.button,
                            ...(isSm ? styles.buttonSm : styles.buttonMd),
                            ...(activePreset === 'custom' || popoverOpen ? styles.buttonActive : {}),
                            ...styles.customButton,
                        }, onClick: () => !disabled && setPopoverOpen(o => !o), disabled: disabled, type: "button", "aria-expanded": popoverOpen, children: [_jsx(CalendarIcon, { style: { marginRight: '4px', width: '12px', height: '12px' } }), displayText || 'Custom', _jsx(ChevronIcon, { style: { marginLeft: '4px', width: '10px', height: '10px' } })] })] }), _jsxs("div", { style: styles.resolvesTo, children: [_jsx("span", { style: styles.resolvesToLabel, children: "Resolves to:" }), _jsxs("span", { style: styles.resolvesToValue, children: [resolvedDateDisplay.text, resolvedDateDisplay.days !== null && (_jsxs("span", { style: styles.resolvesToDays, children: [" (", resolvedDateDisplay.days, " days)"] }))] })] }), popoverOpen && (_jsxs("div", { ref: popoverRef, style: styles.popover, children: [_jsxs("div", { style: styles.fySection, children: [_jsxs("div", { style: styles.fyStepper, children: [_jsx("button", { type: "button", style: styles.fyArrow, onClick: () => setFyYear(y => y - 1), children: "\u2039" }), _jsxs("button", { type: "button", style: styles.fyLabel, onClick: handleFullFyClick, title: `Select all of ${cal.label} ${String(fyYear).slice(-2)} (${fiscalSpanLabel(cal)})`, children: ["FY", String(fyYear).slice(-2)] }), _jsx("button", { type: "button", style: styles.fyArrow, onClick: () => setFyYear(y => y + 1), children: "\u203A" })] }), _jsxs("div", { style: styles.fyQuarterGroup, children: [_jsx("span", { style: styles.fyQuarterLabel, children: "FY quarter" }), _jsx("div", { style: styles.fyQuarters, children: [1, 2, 3, 4].map(q => (_jsxs("button", { type: "button", style: styles.fyQuarter, onClick: () => handleFyQuarterClick(q), children: ["Q", q] }, q))) })] })] }), _jsx("div", { style: styles.divider }), _jsxs("div", { style: styles.calendarNav, children: [_jsx("button", { type: "button", style: styles.navArrow, onClick: handlePrevMonth, children: "\u2039" }), _jsxs("div", { style: styles.navTitle, children: [MONTH_NAMES[viewMonth.getMonth()], " ", viewMonth.getFullYear(), " \u2013 ", MONTH_NAMES[secondMonth.getMonth()], " ", secondMonth.getFullYear()] }), _jsx("button", { type: "button", style: styles.navArrow, onClick: handleNextMonth, children: "\u203A" })] }), _jsxs("div", { style: styles.calendars, children: [_jsx(CalendarMonth, { month: viewMonth, rangeStart: rangeStart, rangeEnd: rangeEnd, hoverDate: hoverDate, onDayClick: handleDayClick, onDayHover: setHoverDate }), _jsx(CalendarMonth, { month: secondMonth, rangeStart: rangeStart, rangeEnd: rangeEnd, hoverDate: hoverDate, onDayClick: handleDayClick, onDayHover: setHoverDate })] }), rangeStart && (_jsxs("div", { style: styles.rangeSummary, children: [selectionLabel && _jsxs("span", { style: styles.rangeSummaryLabel, children: [selectionLabel, ":"] }), _jsx("span", { children: rangeEnd
                                    ? formatDateRangeDisplay(formatDate(rangeStart), formatDate(rangeEnd))
                                    : `${MONTH_NAMES[rangeStart.getMonth()]} ${rangeStart.getDate()} – (select end)` })] })), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: styles.cancelBtn, onClick: handleCancel, children: "Cancel" }), _jsx("button", { type: "button", style: {
                                    ...styles.applyBtn,
                                    ...(rangeStart && rangeEnd ? {} : styles.applyBtnDisabled),
                                }, onClick: handleApply, disabled: !rangeStart || !rangeEnd, children: "Apply" })] })] }))] }));
}
// Simple chevron icon
function ChevronIcon({ style }) {
    return (_jsx("svg", { width: "12", height: "12", viewBox: "0 0 12 12", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: style, children: _jsx("polyline", { points: "3 4.5 6 7.5 9 4.5" }) }));
}
function CalendarIcon({ style }) {
    return (_jsxs("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", style: style, children: [_jsx("rect", { x: "3", y: "4", width: "18", height: "18", rx: "2" }), _jsx("line", { x1: "3", y1: "10", x2: "21", y2: "10" }), _jsx("line", { x1: "8", y1: "2", x2: "8", y2: "6" }), _jsx("line", { x1: "16", y1: "2", x2: "16", y2: "6" })] }));
}
// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const styles = {
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        position: 'relative',
    },
    container: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        background: 'var(--surface-2, #f3f4f6)',
        borderRadius: '6px',
        padding: '2px',
    },
    resolvesTo: {
        fontSize: '11px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    resolvesToLabel: {
        color: 'var(--text-muted, #6b7280)',
    },
    resolvesToValue: {
        color: 'var(--text-primary, #1f2937)',
        fontWeight: 500,
    },
    resolvesToDays: {
        color: 'var(--text-muted, #6b7280)',
        fontWeight: 400,
    },
    disabled: {
        opacity: 0.5,
        pointerEvents: 'none',
    },
    button: {
        fontWeight: 500,
        border: 'none',
        borderRadius: '4px',
        background: 'transparent',
        color: 'var(--text-muted, #6b7280)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        whiteSpace: 'nowrap',
    },
    buttonSm: {
        fontSize: '11px',
        padding: '4px 8px',
    },
    buttonMd: {
        fontSize: '13px',
        padding: '6px 12px',
    },
    buttonActive: {
        background: 'var(--accent, #3b82f6)',
        color: 'white',
    },
    customButton: {
        display: 'flex',
        alignItems: 'center',
        borderLeft: '1px solid var(--border, rgba(0,0,0,0.12))',
        marginLeft: '4px',
        paddingLeft: '10px',
    },
    popover: {
        position: 'absolute',
        top: 'calc(100% + 6px)',
        right: 0,
        background: 'var(--surface-1, white)',
        borderRadius: '10px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
        padding: '16px',
        zIndex: 1000,
        minWidth: '480px',
    },
    fySection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
    },
    fyStepper: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
    },
    fyArrow: {
        width: '24px',
        height: '24px',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: '4px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '14px',
        color: 'var(--text-primary, #1f2937)',
    },
    fyLabel: {
        padding: '4px 12px',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: '4px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--text-primary, #1f2937)',
    },
    fyQuarterGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    fyQuarterLabel: {
        fontSize: '11px',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        color: 'var(--text-muted, #6b7280)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    },
    fyQuarters: {
        display: 'flex',
        gap: '4px',
    },
    fyQuarter: {
        padding: '4px 10px',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: '4px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 500,
        color: 'var(--text-muted, #6b7280)',
    },
    divider: {
        height: '1px',
        background: 'var(--border, #e5e7eb)',
        margin: '12px 0',
    },
    calendarNav: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
    },
    navArrow: {
        width: '28px',
        height: '28px',
        border: 'none',
        borderRadius: '4px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '16px',
        color: 'var(--text-primary, #1f2937)',
    },
    navTitle: {
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--text-primary, #1f2937)',
    },
    calendars: {
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
    },
    rangeSummary: {
        textAlign: 'center',
        fontSize: '12px',
        color: 'var(--text-primary, #1f2937)',
        marginTop: '12px',
        padding: '8px 12px',
        background: 'var(--surface-2, #f3f4f6)',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
    },
    rangeSummaryLabel: {
        fontWeight: 600,
        color: 'var(--accent, #3b82f6)',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        marginTop: '16px',
    },
    cancelBtn: {
        padding: '8px 16px',
        border: '1px solid var(--border, #e5e7eb)',
        borderRadius: '6px',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: '13px',
        color: 'var(--text-primary, #1f2937)',
    },
    applyBtn: {
        padding: '8px 16px',
        border: 'none',
        borderRadius: '6px',
        background: 'var(--accent, #3b82f6)',
        color: 'white',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 500,
    },
    applyBtnDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
};
export default TimeRangePicker;
